/**
 * Pure SVG High-Performance Particle Engine for atmospheric and reaction FX.
 */
import { SVGObject, SVGObjectConfig } from '../objects/SVGObject';
import { Renderer } from '../core/Renderer';

export interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  alpha: number;
  life: number;
  maxLife: number;
  color: string;
  element?: SVGElement;
}

export abstract class ParticleSystem extends SVGObject {
  protected particles: Particle[] = [];
  protected maxParticles: number = 60;
  protected emitRate: number = 20; // per second
  protected emitTimer: number = 0;
  protected particleGroup: SVGGElement;

  constructor(config: SVGObjectConfig, maxParticles: number = 60) {
    super({ ...config, type: 'particles', layer: 'fx' });
    this.maxParticles = maxParticles;
    this.particleGroup = Renderer.createElement('g');
    this.container.appendChild(this.particleGroup);
  }

  public render(): void {
    // Container ready
  }

  public override update(time: number, delta: number): void {
    super.update(time, delta);
    if (!this.visible || this.opacity <= 0) return;

    this.emitTimer += delta;
    const spawnCount = Math.floor(this.emitTimer * this.emitRate);
    if (spawnCount > 0) {
      this.emitTimer = 0;
      for (let i = 0; i < spawnCount; i++) {
        if (this.particles.length < this.maxParticles) {
          const p = this.createParticle();
          this.particles.push(p);
        }
      }
    }

    // Update particles
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.life += delta;

      if (p.life >= p.maxLife) {
        if (p.element && p.element.parentNode) {
          p.element.parentNode.removeChild(p.element);
        }
        this.particles.splice(i, 1);
        continue;
      }

      this.updateParticle(p, delta);
      this.renderParticleDOM(p);
    }
  }

  protected abstract createParticle(): Particle;
  protected abstract updateParticle(p: Particle, delta: number): void;

  protected renderParticleDOM(p: Particle): void {
    if (!p.element) {
      const circle = Renderer.createElement('circle');
      p.element = circle;
      this.particleGroup.appendChild(circle);
    }
    Renderer.setAttributes(p.element, {
      cx: p.x.toFixed(1),
      cy: p.y.toFixed(1),
      r: p.size.toFixed(1),
      fill: p.color,
      opacity: p.alpha.toFixed(2)
    });
  }
}
